import React, { useState, useEffect } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    COUNTRY_CODES,
    InputWrapper,
    LoadingIndicator,
    ProfileFeatureToggleFlags,
    Toast,
    useProfileHasFeatureToggle,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Tabs } from '@ygtechnologies/rubus-ui';
import { Link } from 'react-router-dom';
import { ValidationForm, ValidatingInput, ValidationSummary, ValidatingSelect } from '@/components/forms/validation';
import ImageUpload from '@/components/generic-controls/ImageUpload';
import {
    getProfileImageUrl,
    createOrUpdateProfile,
    getProfileConfiguration,
    getProfileBackgroundImageUrl,
    ProfileConfiguration,
    UpdateProfileOptions,
} from '@/services/profiles';
import theme from './ProfileEdit.module.scss';
import { Button } from '@/components/Buttons';
import { setActiveProfile } from '@/state/global/actions';
import { useSelector } from 'react-redux';
import { TranslatableText, UnitOfMeasurement } from '@/types';
import { useTimestamp, useMergeState, useAsyncState, useParamReplace, useAppDispatch } from '@/utils/hooks';
import { IState } from '@/state/IState';
import { validateSubdomain } from '@/utils/url';
import SimpleFormPage from '@/components/SimpleFormPage/SimpleFormPage';
import { getCurrencies } from '@/services/currencies';
import { toNullableNumber } from '@/utils/html';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import StandardRepliesPage from './StandardReplies/StandardRepliesPage';
import { useParams, Redirect } from 'react-router';
import AnalyticsSettings from './Analytics/AnalyticsSettings';
import { SurveySetup } from './SurveySetup/SurveySetup';

interface ProfileEditProps {}

export enum CompanySettingsTab {
    Profile = 'profile',
    StandardReplies = 'standard-replies',
    SurveySetup = 'survey-setup',
    Analytics = 'analytics',
}

type UrlParams = {
    tab: CompanySettingsTab;
};

const ProfileEdit: React.FC<ProfileEditProps> = () => {
    const { tab } = useParams<UrlParams>();
    const replaceParams = useParamReplace<UrlParams>();

    const [saving, setSaving] = useState(false);
    const [headerImage, setHeaderImage] = useState<any>(null);
    const [backgroundImage, setBackgroundImage] = useState<any>(null);
    const [errors, setErrors] = useState<TranslatableText[]>([]);
    const [config, mergeConfig, setConfig] = useMergeState<ProfileConfiguration | null>(null);
    const [currencies] = useAsyncState(() => getCurrencies(), [], []);
    const showBuyerSurvey = useProfileHasFeatureToggle(ProfileFeatureToggleFlags.ShowProfileSurvey);

    const { id: profileId, permissions: profilePermissions } = useSelector((state: IState) => state.global.activeProfile!);
    const dispatch = useAppDispatch();

    const cacheBuster = useTimestamp([profileId]);

    useEffect(() => {
        async function loadData() {
            setConfig(null);
            const config = await getProfileConfiguration(profileId);
            setConfig(config);
        }

        loadData();
    }, [profileId, setConfig]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!config) {
            return;
        }

        setErrors([]);
        setSaving(true);

        var data: UpdateProfileOptions = {
            ...config,
            image: headerImage,
            backgroundImage,
        };

        try {
            var result = await createOrUpdateProfile(profileId, data);
            dispatch(setActiveProfile(result));
            Toast.success(<Trans>Profile settings saved</Trans>);
        } catch (e: any) {
            // duplicate subdomain
            if (e.status === 409) {
                const errorMessage = <Trans>Subdomain name taken</Trans>;
                Toast.error(errorMessage);
                setErrors(errors.concat([errorMessage]));
            }
        } finally {
            setSaving(false);
        }
    };

    if (!tab) {
        return <Redirect to={replaceParams({ tab: CompanySettingsTab.Profile })} />;
    }

    if (!config || !currencies) {
        return <LoadingIndicator />;
    }

    const renderTabContent = () => {
        switch (tab) {
            case CompanySettingsTab.Profile:
                return renderProfileTab();
            case CompanySettingsTab.StandardReplies:
                return <StandardRepliesPage profileId={profileId} />;
            case CompanySettingsTab.SurveySetup:
                return showBuyerSurvey ? <SurveySetup profileId={profileId} /> : null;
            case CompanySettingsTab.Analytics:
                return profilePermissions.canManageAnalytics ? <AnalyticsSettings profileId={profileId} /> : null;
            default:
                return null;
        }
    };

    const renderProfileTab = () => (
        <SimpleFormPage>
                        <ValidationForm onSubmit={handleFormSubmit} className="form columns-1 labels-20">
                            <div className="form-label">
                                <Trans>Logo</Trans>
                                <div className="form-control-unstyled">
                                    <ImageUpload
                                        shouldCrop={false}
                                        containerClassName={theme.profileImage}
                                        aspectRatio={1}
                                        currentImage={headerImage || getProfileImageUrl(profileId) + '?__' + cacheBuster}
                                        onChange={(image) => setHeaderImage(image)}
                                        previewClassName={theme.headerImageEditPreview}
                                    />
                                </div>
                            </div>
                            <label>
                                <Trans>Profile name</Trans>
                                <ValidatingInput
                                    type="text"
                                    required
                                    className="form-control"
                                    placeholder={t`Profile name`}
                                    value={config.name || ''}
                                    onChange={(e) => mergeConfig({ name: e.target.value })}
                                />
                            </label>

                            <label>
                                <Trans>Country</Trans>
                                <ValidatingSelect
                                    className="form-control"
                                    required
                                    fieldName={t`Country`}
                                    value={config.countryCode}
                                    onChange={(e) =>
                                        mergeConfig({ countryCode: e.target.value as keyof typeof COUNTRY_CODES })
                                    }>
                                    <option value="">{t`Please select`}</option>
                                    {Object.entries(COUNTRY_CODES).map(([code, name]) => (
                                        <option key={code} value={code}>
                                            {name}
                                        </option>
                                    ))}
                                </ValidatingSelect>
                            </label>

                            <label>
                                <Trans>Currency</Trans>
                                <span className={theme.required}>*</span>
                                <ValidatingSelect
                                    className="form-control"
                                    required
                                    fieldName={t`Currency`}
                                    value={config.currencyId == null ? '' : config.currencyId}
                                    onChange={(e) => mergeConfig({ currencyId: toNullableNumber(e.target.value) })}>
                                    <option value="">{t`Please select`}</option>
                                    {currencies.map((currency) => (
                                        <option key={currency.id} value={currency.id}>
                                            {currency.code} - {currency.name}
                                        </option>
                                    ))}
                                </ValidatingSelect>
                            </label>

                            <label>
                                <Trans>Default unit of measurement</Trans>
                                <span className={theme.required}>*</span>
                                <ValidatingSelect
                                    className="form-control"
                                    required
                                    fieldName={t`Unit of measurement`}
                                    value={config.defaultUnitOfMeasurement}
                                    onChange={(e) =>
                                        mergeConfig({ defaultUnitOfMeasurement: e.target.value as UnitOfMeasurement })
                                    }>
                                    <option key={'mm'} value={UnitOfMeasurement.Millimeter}>
                                        {t`mm (millimeters)`}
                                    </option>
                                    <option key={'cm'} value={UnitOfMeasurement.Centimeter}>
                                        {t`cm (centimeters)`}
                                    </option>
                                </ValidatingSelect>
                            </label>

                            {profilePermissions.isPaying && (
                                <label>
                                    <Trans>Subdomain</Trans>
                                    <InputWrapper
                                        appendContent={
                                            window.APP_CONFIG.APP_DOMAIN ? `.${window.APP_CONFIG.APP_DOMAIN}` : undefined
                                        }>
                                        <ValidatingInput
                                            style={{ width: '150px' }}
                                            type="text"
                                            className="form-control"
                                            placeholder={t`Subdomain`}
                                            value={config.subdomain || ''}
                                            onChange={(e) => mergeConfig({ subdomain: e.target.value })}
                                            validations={[
                                                {
                                                    isValid: (value) => validateSubdomain(value),
                                                    summaryMessage: () => (
                                                        <Trans>
                                                            Subdomain can only contain alphanumeric characters and dashes
                                                        </Trans>
                                                    ),
                                                },
                                            ]}
                                        />
                                    </InputWrapper>
                                </label>
                            )}

                            {/* <div>
                <div className="form-label">
                    <Trans>External logins</Trans>
                    <div className="form-control-unstyled multi">
                        {Object.values(ExternalLoginProvider).map((provider: ExternalLoginProvider) => (
                            <Checkbox
                                key={provider}
                                checked={loginProviders.includes(provider)}
                                onChange={(e) => setLoginProviders(toggleValue(loginProviders, provider, e.target.checked))}>
                                <Trans id={`ExternalLoginProvider.${provider}`} />
                            </Checkbox>
                        ))}
                    </div>
                </div>
            </div> */}
                            {profilePermissions.isPaying && (
                                <div className="form-label">
                                    <Trans>Background image</Trans>
                                    <div className={'form-control'}>
                                        <ImageUpload
                                            previewSize={0.5}
                                            onChange={(image) => setBackgroundImage(image)}
                                            aspectRatio={16 / 10}
                                            currentImage={
                                                backgroundImage ||
                                                getProfileBackgroundImageUrl(profileId) + '?__' + cacheBuster
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="label-offset">
                                <ValidationSummary externalErrors={errors} />
                            </div>
                            <div className="flex end">
                                <Button type="submit" color="primary" loading={saving}>
                                    <Trans>Save</Trans>
                                </Button>
                            </div>
                        </ValidationForm>
                    </SimpleFormPage>
    );

    return (
        <>
            <SimplePageTitle title={t`Company settings ${config.name}`} />

            <Tabs.Container activeId={tab} variant="underline" sticky>
                <Tabs.Tab
                    id={CompanySettingsTab.Profile}
                    as={Link}
                    to={replaceParams({ tab: CompanySettingsTab.Profile })}
                    label={<Trans>Profile</Trans>}
                />
                <Tabs.Tab
                    id={CompanySettingsTab.StandardReplies}
                    as={Link}
                    to={replaceParams({ tab: CompanySettingsTab.StandardReplies })}
                    label={<Trans>Standard replies</Trans>}
                />
                {showBuyerSurvey && (
                    <Tabs.Tab
                        id={CompanySettingsTab.SurveySetup}
                        as={Link}
                        to={replaceParams({ tab: CompanySettingsTab.SurveySetup })}
                        label={<Trans>Survey setup</Trans>}
                    />
                )}
                {profilePermissions.canManageAnalytics && (
                    <Tabs.Tab
                        id={CompanySettingsTab.Analytics}
                        as={Link}
                        to={replaceParams({ tab: CompanySettingsTab.Analytics })}
                        label={<Trans>Analytics</Trans>}
                    />
                )}
                <Tabs.Content>{renderTabContent()}</Tabs.Content>
            </Tabs.Container>
        </>
    );
};

export default ProfileEdit;
